// src/components/ProductCard.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import VariantPickerModal from "./VariantPickerModal";

/* ---------- Helpers ---------- */
const nf = new Intl.NumberFormat("fr-FR");
const fmtXOF = (v) => `${nf.format(Math.round(Number(v || 0)))} FCFA`;

const safe = (u) => (u && typeof u === "string" && u.trim() ? u.trim() : "");
const firstImage = (p) => safe(p?.images?.[0]) || safe(p?.image) || "";

const toNumber = (v) => {
  if (v == null) return NaN;
  const n = Number(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : NaN;
};

function getPromoInfo(p) {
  const normal =
    Number.isFinite(toNumber(p?.priceText)) ? toNumber(p?.priceText) : toNumber(p?.price);
  const promo = toNumber(p?.priceTextPromo);

  if (Number.isFinite(normal) && Number.isFinite(promo) && promo > 0 && promo < normal) {
    const pct = Math.round(((normal - promo) / normal) * 100);
    return { hasPromo: true, normal, promo, pct: Math.max(0, pct) };
  }
  return { hasPromo: false, normal: normal || 0, promo: null, pct: 0 };
}

// --- toast « 1ère fois » par produit + variante ---
function toastFirstTime(product, variantKey = "default") {
  const pid =
    product?.id ||
    product?.slug ||
    product?.reference ||
    String(product?.title || "").toLowerCase().replace(/\s+/g, "-");

  const key = `added_once_${pid}|${variantKey}`;
  if (!localStorage.getItem(key)) {
    localStorage.setItem(key, "1");
    try {
      window.dispatchEvent(
        new CustomEvent("toast", {
          detail: {
            type: "success",
            message: `“${product?.title || "Produit"}” a été ajouté à votre panier.`,
          },
        })
      );
    } catch {
      // fallback silencieux
      console.log(`Ajout panier: ${product?.title || "Produit"}`);
    }
  }
}

export default function ProductCard({ p, onAdd }) {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);

  const hasVariants = Array.isArray(p?.variants) && p.variants.length > 0;

  const inStock = useMemo(() => {
    if (!p) return false;
    if (hasVariants) return p.variants.some((v) => Number(v?.stock || 0) > 0);
    // si stock absent => on considère dispo (pour éviter de bloquer)
    const s = p?.stock;
    if (s == null) return true;
    return Number(s) > 0;
  }, [hasVariants, p]);

  const href = p?.slug ? `/p/${encodeURIComponent(p.slug)}` : "#";
  const img = firstImage(p);
  const promo = useMemo(() => getPromoInfo(p), [p]);

  const priceNode = promo.hasPromo ? (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <strong className="pc-price">{nf.format(Math.round(promo.promo))} FCFA</strong>
      <span className="pc-old">{nf.format(Math.round(promo.normal))} FCFA</span>
      {promo.pct > 0 && <span className="badge text-bg-danger">-{promo.pct}%</span>}
    </div>
  ) : (
    <strong className="pc-price">
      {p?.priceText ? `${p.priceText} FCFA` : fmtXOF(p?.price)}
    </strong>
  );

  const goProduct = (e) => {
    if (!p?.slug) {
      e.preventDefault();
      return;
    }
    navigate(href);
  };

  // const onQuickAdd = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   if (!inStock) return;

  //   // si variantes => ouvrir le picker pour choisir taille/couleur
  //   if (hasVariants) {
  //     setPickerOpen(true);
  //     return;
  //   }

  //   onAdd?.(p, 1, { variantKey: "default", variant: null, variantLabel: "" });
  //   toastFirstTime(p, "default");
  // };

  return (
    <article className={`card h-100 shadow-sm border-0 pc ${!inStock ? "pc-out" : ""}`}>
      {/* Media */}
      <Link
        to={href}
        className="pc-media d-block"
        onClick={goProduct}
        aria-label={p?.title ? `Voir ${p.title}` : "Voir le produit"}
      >
        <div className="pc-ratio bg-light">
          <img
            src={img || "/no-photo.jpg"}
            alt={p?.title || "Produit"}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              if (e.currentTarget.src !== "/no-photo.jpg") e.currentTarget.src = "/no-photo.jpg";
            }}
            className="pc-img"
          />
        </div>

        {/* Badges */}
        <div className="pc-badges">
          {!inStock ? <span className="badge text-bg-dark">Rupture</span> : null}
          {promo.hasPromo && promo.pct > 0 ? (
            <span className="badge text-bg-danger">Promo</span>
          ) : null}
        </div>
      </Link>

      {/* Body */}
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <h6 className="pc-title mb-1" title={p?.title}>
            {p?.title || "—"}
          </h6>
        </div>

        {p?.code ? <div className="pc-code">Code : <strong>{p.code}</strong></div> : null}

        <div className="mt-2">{priceNode}</div>

        <div className="mt-auto pt-3 d-flex gap-2">
          <Link to={href} onClick={goProduct} className="btn btn-dark btn-sm flex-grow-1">
            Afficher tailles
          </Link>

          {/* <button
            type="button"
            className="btn btn-warning btn-sm pc-add"
            onClick={onQuickAdd}
            disabled={!inStock}
            title={hasVariants ? "Choisir taille/couleur" : "Ajouter au panier"}
            aria-label={hasVariants ? "Choisir taille et ajouter" : "Ajouter au panier"}
          >
            <i className="bi bi-bag-plus"></i>
          </button> */}
        </div>
      </div>

      {/* Variant picker */}
      {hasVariants && (
        <VariantPickerModal
          product={p}
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={(variantKey, variant) => {
            if (variant && Number(variant?.stock || 0) <= 0) return;

            const label =
              variant?.label ||
              [variant?.color, variant?.size].filter(Boolean).join(" / ") ||
              (variantKey && variantKey !== "default"
                ? String(variantKey).replace(/\|/g, " / ")
                : "");

            onAdd?.(p, 1, { variantKey, variant, variantLabel: label || undefined });
            toastFirstTime(p, variantKey || "default");
            setPickerOpen(false);
          }}
        />
      )}

      {/* Styles locaux (élégants + cohérents) */}
      <style>{`
        .pc { border-radius: 16px; overflow: hidden; transition: transform .18s ease, box-shadow .18s ease; }
        .pc:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(0,0,0,.12) !important; }

        .pc-media { position: relative; text-decoration: none; color: inherit; }
        .pc-ratio { aspect-ratio: 4 / 3; }
        .pc-img { width: 100%; height: 100%; display: block; object-fit: cover; padding: 10px; }

        .pc-badges { position: absolute; inset: 10px 10px auto 10px; display: flex; gap: 6px; justify-content: space-between; pointer-events: none; }
        .pc-badges .badge { pointer-events: none; }

        .pc-title {
          font-weight: 700;
          font-size: .95rem;
          line-height: 1.2;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 2.3em;
        }

        .pc-code { font-size: .82rem; color: #6b7280; }
        .pc-price { color: #ff9800; font-size: 1.05rem; }
        .pc-old { color: #9aa0a6; font-size: .85rem; text-decoration: line-through; }

        .pc-add { width: 44px; display: inline-flex; align-items: center; justify-content: center; }
        .pc-out { opacity: .85; }
        .pc-out .pc-add { cursor: not-allowed; }

        @media (max-width: 576px){
          .pc-add { width: 42px; }
        }
      `}</style>
    </article>
  );
}
